import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subscription, throwError, timer } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { SecureStorageService } from '../../../core/services/secure-storage.service';
import { SessionEventService } from '../../../core/services/session-event.service';
import Swal from 'sweetalert2';

/** Shared across tabs (plain localStorage) so one tab's activity keeps the others alive. */
const LAST_ACTIVITY_KEY = 'lastActivityAt';
/** The 30-minute inactivity logout. Off: the gateway token's own 12h expiry ends the session. */
const IDLE_LOGOUT_ENABLED = false;

/**
 * Admin authentication — v2-native.
 *
 * Was a direct call to the legacy `auth/admin/login`, which checked the password against the legacy
 * own admin table and then had legacy call the microservice to mint a matching token. Now this
 * calls the microservice gateway directly (action 8074 and friends) against its OWN admin
 * table — the same super_admin row set up directly in the database, and every sub-admin that
 * row goes on to create (see SettingService). One token now serves both the admin-panel
 * session AND the loyalty gateway, so `authToken`/`msAuthToken` are always the same value
 * here; both keys are still written so nothing else that reads either breaks.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly gatewayUrl = environment.loyaltyApiUrl;
  private idleTimeout: any;
  private absoluteTimeout: any;
  private idleLimit = 30 * 60 * 1000; // 30 minutes
  private absoluteLimit = 60 * 60 * 1000; // 30 minutes
  // Window activity listeners are only ever attached once — they call
  // resetIdleTimer() for the lifetime of the tab, but that method now no-ops
  // once logged out, so re-adding them on every login isn't needed anyway.
  private activityMonitored = false;
  private isLoggingOut = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private secureStorage: SecureStorageService,
    private sessionEventService: SessionEventService
  ) { }

  /** The token the session currently runs on — the interceptor compares replies against it. */
  currentToken(): string | null {
    return this.secureStorage.getItem('authToken');
  }

  getHeaders(): HttpHeaders {
    const token = this.secureStorage.getItem('authToken') || '';

    let headers = new HttpHeaders({
      Accept: '*/*',
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else {
      console.warn('⚠️ No Auth Token Found in Local Storage!');
    }
    return headers;
  }

  /** Every call to the gateway sends the internal key (required on PUBLIC actions —
   *  login/forgot/reset have no session yet — and harmless on ADMIN ones), plus the
   *  bearer token when a session exists. */
  private gatewayHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Skip-Loader': 'true',
    });
    const token = this.secureStorage.getItem('authToken');
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }

  /** Unwraps the gateway's { isSuccess, data } / { isSuccess:false, message, code }
   *  envelope into the bare-body shape every component here already expects from legacy,
   *  re-throwing a failure in the `{ error: { message, code } }` shape HttpErrorResponse
   *  callers read (`error.error?.message`) so no error handler needs to change. */
  private unwrap<T = any>(actioncode: number, data: Record<string, any> = {}): Observable<T> {
    return this.http
      .post<any>(this.gatewayUrl, { actioncode, data }, { headers: this.gatewayHeaders() })
      .pipe(
        map((res) => {
          if (!res || res.isSuccess === false) {
            throw { error: { message: res?.message, code: res?.code }, status: 400 };
          }
          return res.data as T;
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  /** Login — action 8074 (admin-password-login), v2-native against the admin table. */
  login(loginData: any): Observable<{ user: any }> {
    return this.unwrap<{ user: any }>(8074, {
      email: loginData?.email,
      password: loginData?.password,
    }).pipe(
      tap((response: any) => {
        const user = response.user;
        if (user?.authToken) {
          this.secureStorage.setItem('id', user.id.toString());
          this.secureStorage.setItem('authToken', user?.authToken);

          // One token now serves both the admin panel AND the loyalty gateway.
          const msToken = response?.msAuthToken || user?.msAuthToken || user?.authToken;
          if (msToken) {
            this.secureStorage.setItem('msAuthToken', msToken);
          }

          this.startSessionTimers();
        } else {
          throw new Error('Login failed: No auth token received.');
        }
      })
    );
  }

  /** Forgot Password — action 8076 (admin-forgot-password). `routerLink` is accepted and
   *  ignored server-side; the emailed reset link's base is an env value, not a request field. */
  forgotPassword(body: any): Observable<any> {
    return this.unwrap(8076, { email: body?.email, routerLink: body?.routerLink });
  }

  /** action 8077 (admin-reset-password) — the emailed token IS the credential, no session. */
  resetPassword(body: any) {
    return this.unwrap(8077, { token: body?.token, password: body?.password });
  }

  /** Logout — action 8075 (admin-logout). */
  logout(): void {
    // Already logged out (or a logout is already in flight) — nothing to do.
    // Without this guard, a stray idle/absolute timer firing after the token
    // is cleared would POST to the logout endpoint with no valid token, get a
    // 401 back, and the auth interceptor would call logout() again for that
    // 401 — an infinite logout↔401 loop.
    const token = this.secureStorage.getItem('authToken');
    if (this.isLoggingOut || !token) {
      this.finishLogout();
      return;
    }
    this.isLoggingOut = true;

    // Call the backend logout API (needs the auth header, so fire it before
    // clearing the token). The backend records the "logout" action in the
    // super admin and activity logs. Redirect regardless of success/failure.
    // The session id is not sent: the gateway revokes the token it was called WITH, so
    // passing an id would only offer a way to ask it to revoke another admin.
    this.unwrap(8075, {}).subscribe({
      next: () => this.finishLogout(),
      error: () => this.finishLogout(),
    });
  }

  /** Clear session data and redirect to login. */
  private finishLogout(): void {
    this.secureStorage.removeItem('authToken');
    this.secureStorage.removeItem('msAuthToken');
    this.secureStorage.removeItem('id');
    this.clearTimers();
    this.isLoggingOut = false;
    // SweetAlert2 dialogs mount directly on <body>, outside the router
    // outlet, so an Angular navigation alone doesn't remove one — a modal
    // left open when the session ends (idle timeout, 401, manual logout)
    // stays stuck on top of the login page after the redirect.
    Swal.close();
    this.router.navigate(['/auth/login']);
  }

  /** Re-arm the idle/absolute session timers for an already-authenticated
   * session (e.g. on a hard page refresh where login() never re-runs). */
  initSessionIfAuthenticated(): void {
    const token = this.secureStorage.getItem('authToken');
    if (token) {
      this.startSessionTimers();
    }
  }

  /** Start session timers */
  private startSessionTimers(): void {
    this.clearTimers();

    // Absolute timeout disabled — backend already enforces token-based
    // session expiry, so this was a redundant hard cutoff. Uncomment to
    // re-enable a frontend-forced absolute logout after `absoluteLimit`.
    // this.absoluteTimeout = setTimeout(() => {
    //   this.sessionEventService.notifySessionTimeout();
    //   this.logout();
    //   this.toastr.success('Session expired after 30 minutes.');
    // }, this.absoluteLimit);

    // Idle timeout disabled too — the session lasts as long as the gateway's token (12h,
    // ADMIN_JWT_TTL), and an expired token signs the admin out through the interceptor. A
    // 30-minute idle logout dropped admins midway through long forms (the contest wizard),
    // and the save they then sent went out with no token at all. Set IDLE_LOGOUT_ENABLED to
    // true to bring it back.
    this.monitorUserActivity();
    if (IDLE_LOGOUT_ENABLED) this.resetIdleTimer();
  }

  /** Reset idle timer on activity */
  private resetIdleTimer(): void {
    if (!IDLE_LOGOUT_ENABLED) return;
    if (this.idleTimeout) clearTimeout(this.idleTimeout);

    // Activity on the login page (or after any logout) must not re-arm this —
    // otherwise the leftover window listeners keep resetting a timer for a
    // session that no longer exists, and it eventually fires logout() with no
    // valid token (401 → interceptor calls logout() again → infinite loop).
    if (!this.secureStorage.getItem('authToken')) return;

    // Every tab shares one token, so activity is shared too. Before this, a second tab left
    // open in the background timed out on its own after 30 minutes, and its logout (8075)
    // revoked the token the admin was still working with in the other tab.
    // mousemove fires constantly; a few seconds of precision is plenty for a 30-minute window.
    const now = Date.now();
    if (now - (Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0) > 5000) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    }
    this.armIdleTimer(this.idleLimit);
  }

  private armIdleTimer(delay: number): void {
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    this.idleTimeout = setTimeout(() => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
      const idleFor = Date.now() - last;
      if (idleFor < this.idleLimit) {
        // Another tab was used meanwhile — wait out the rest of its window instead.
        this.armIdleTimer(this.idleLimit - idleFor);
        return;
      }
      this.sessionEventService.notifySessionTimeout();
      this.logout();
      this.toastr.success('Logged out due to 30 minutes of inactivity.');
    }, delay);
  }

  /** Watch for user activity — attached once for the tab's lifetime. */
  private monitorUserActivity(): void {
    if (this.activityMonitored) return;
    this.activityMonitored = true;
    ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach((event) =>
      window.addEventListener(event, () => this.resetIdleTimer())
    );
    // Signed out in another tab: the token is already revoked, so just leave too —
    // without calling 8075 again — rather than failing every request with "Invalid token".
    window.addEventListener('storage', (e) => {
      if (e.key === 'authToken' && !e.newValue) {
        this.ngZone.run(() => this.finishLogout());
      }
    });
  }

  /** Clear timers */
  private clearTimers(): void {
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    if (this.absoluteTimeout) clearTimeout(this.absoluteTimeout);
  }
}
